/**
 * Track M spatial gravity — pure accumulate into `out` (displacement units, same as Woods u).
 *
 * Kernel (soft unit direction, same shape as Woods F):
 *   r̂(y, x) = (y − x) / (ε_g + ‖y − x‖)     // ‖r̂‖ < 1
 *
 * Force → response (F=ma style displacement)
 * ------------------------------------------
 *   force_i  = γ · (source terms · r̂)
 *   g_i      = force_i / m_i
 *
 * `particleMass` is uniform inertia of every point/voxel (F=ma, not “more mass =
 * stronger Newton pull”). It does two jobs:
 *   1. Source weight for COM / type_com centroids and mutual neighbor pull.
 *   2. Response g = F/m — HIGHER m → smaller steps → calmer cloud.
 *   LOWER m → larger steps → clumps. At m=1, g equals the force field scale.
 * Social Woods u is never mass-scaled — only the gravity layer.
 * Values are floored at MIN_PARTICLE_MASS so m→0 cannot explode 1/m.
 *
 * Clump / distribution assumptions (v1 — no multi-clump hierarchy)
 * ---------------------------------------------------------------
 * origin   — one fixed attractor at 0. Mass does not change the target; only /m response.
 * com      — ONE global well at the mass-weighted centroid of all particles.
 *            Uniform m ⇒ ordinary geometric mean. Strong γ/m → whole cloud → one ball.
 *            There is no secondary clumping inside the bowl; the attractor is a single point.
 * type_com — one well per family at that family's mass-weighted centroid (T balls max).
 * mutual   — local N-body-ish: each neighbor j contributes m_j r̂.
 *            Mass-normalized so force stays O(γ) independent of neighbor count:
 *              force_i = γ · Σ_j m_j r̂_ij / (m_ref + Σ_j m_j)
 *            then g_i = force_i / m_i. No assumption of pre-existing clumps — density
 *            emerges only if the social field + mutual pull keep neighbors together.
 * wells    — explicit point masses M_k; force = γ Σ M_k r̂; g = force / m_particle.
 *
 * Scale vs Woods: typical ‖u‖ ≈ 0.015–0.025 with default p_s.
 * Gentle accent at m=1: γ ≈ 0.001 (‖g‖/‖u‖ ≈ 0.05). Collapse bowl: γ/m ≳ 0.005.
 */

import type { SimParams } from "./params";
import type { SpatialHash2D } from "./spatialHash";

export type GravityWell = { x: number; y: number; z?: number; m?: number };

export type GravityMode =
  | "none"
  | "origin"
  | "com"
  | "type_com"
  | "mutual"
  | "wells";

export const GRAVITY_MODES: readonly GravityMode[] = [
  "none",
  "origin",
  "com",
  "type_com",
  "mutual",
  "wells",
] as const;

/** Soft unit pull; scale is force weight (γ · source mass factors). */
function softPullToward(
  px: number,
  py: number,
  pz: number,
  tx: number,
  ty: number,
  tz: number,
  dim: number,
  softening: number,
  scale: number,
  out: Float64Array,
  base: number,
): void {
  const dx = tx - px;
  const dy = ty - py;
  const dz = dim > 2 ? tz - pz : 0;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const inv = scale / (softening + dist);
  out[base]! += dx * inv;
  out[base + 1]! += dy * inv;
  if (dim > 2) out[base + 2]! += dz * inv;
}

/** Hard floor — below this, 1/m is a collapse bomb and feels “inverted”. */
export const MIN_PARTICLE_MASS = 0.25;

function particleMassOf(params: { particleMass?: number }): number {
  const m = params.particleMass ?? 1;
  if (!(m > 0) || !Number.isFinite(m)) return 1;
  return m < MIN_PARTICLE_MASS ? MIN_PARTICLE_MASS : m;
}

/** Apply response mass: g = force / m (in-place on `out`). */
function applyResponseMass(
  out: Float64Array,
  n: number,
  dim: number,
  m: number,
): void {
  if (m === 1) return;
  const inv = 1 / m;
  const len = n * dim;
  for (let i = 0; i < len; i++) out[i]! *= inv;
}

/** Zero `out` then write gravity displacement contribution g. */
export function accumulateGravity(
  positions: Float64Array,
  n: number,
  dim: number,
  params: Pick<
    SimParams,
    | "gravityMode"
    | "gravityStrength"
    | "gravitySoftening"
    | "gravityRadius"
    | "neighborRadius"
    | "wells"
    | "particleMass"
  >,
  out: Float64Array,
  opts?: {
    types?: Uint8Array | null;
    spatialHash?: SpatialHash2D | null;
  },
): void {
  out.fill(0);
  const mode = params.gravityMode;
  if (mode === "none") return;

  const gamma = params.gravityStrength;
  if (!(gamma > 0)) return;
  const eps = Math.max(1e-12, params.gravitySoftening);
  const m = particleMassOf(params);

  if (mode === "origin") {
    for (let i = 0; i < n; i++) {
      const b = i * dim;
      softPullToward(
        positions[b]!,
        positions[b + 1]!,
        dim > 2 ? positions[b + 2]! : 0,
        0,
        0,
        0,
        dim,
        eps,
        gamma,
        out,
        b,
      );
    }
    applyResponseMass(out, n, dim, m);
    return;
  }

  if (mode === "com") {
    // Mass-weighted centroid (uniform m ⇒ ordinary mean). Single attractor.
    let sx = 0,
      sy = 0,
      sz = 0;
    const wSum = m * n;
    for (let i = 0; i < n; i++) {
      const b = i * dim;
      sx += m * positions[b]!;
      sy += m * positions[b + 1]!;
      if (dim > 2) sz += m * positions[b + 2]!;
    }
    const invW = 1 / Math.max(wSum, 1e-15);
    const cx = sx * invW;
    const cy = sy * invW;
    const cz = sz * invW;
    for (let i = 0; i < n; i++) {
      const b = i * dim;
      softPullToward(
        positions[b]!,
        positions[b + 1]!,
        dim > 2 ? positions[b + 2]! : 0,
        cx,
        cy,
        cz,
        dim,
        eps,
        gamma,
        out,
        b,
      );
    }
    applyResponseMass(out, n, dim, m);
    return;
  }

  if (mode === "type_com") {
    const types = opts?.types;
    if (!types || types.length < n) {
      accumulateGravity(
        positions,
        n,
        dim,
        { ...params, gravityMode: "com" },
        out,
        opts,
      );
      return;
    }
    const maxT = 8;
    const sum = new Float64Array(maxT * 3);
    const massSum = new Float64Array(maxT);
    for (let i = 0; i < n; i++) {
      const t = Math.min(maxT - 1, types[i] ?? 0);
      const b = i * dim;
      const o = t * 3;
      sum[o]! += m * positions[b]!;
      sum[o + 1]! += m * positions[b + 1]!;
      if (dim > 2) sum[o + 2]! += m * positions[b + 2]!;
      massSum[t]! += m;
    }
    const com = new Float64Array(maxT * 3);
    for (let t = 0; t < maxT; t++) {
      const w = massSum[t]!;
      if (w <= 0) continue;
      const o = t * 3;
      const inv = 1 / w;
      com[o] = sum[o]! * inv;
      com[o + 1] = sum[o + 1]! * inv;
      com[o + 2] = sum[o + 2]! * inv;
    }
    for (let i = 0; i < n; i++) {
      const t = Math.min(maxT - 1, types[i] ?? 0);
      if (massSum[t]! <= 0) continue;
      const o = t * 3;
      const b = i * dim;
      softPullToward(
        positions[b]!,
        positions[b + 1]!,
        dim > 2 ? positions[b + 2]! : 0,
        com[o]!,
        com[o + 1]!,
        com[o + 2]!,
        dim,
        eps,
        gamma,
        out,
        b,
      );
    }
    applyResponseMass(out, n, dim, m);
    return;
  }

  if (mode === "wells") {
    const wells = params.wells;
    if (!wells || wells.length === 0) return;
    for (let i = 0; i < n; i++) {
      const b = i * dim;
      const px = positions[b]!;
      const py = positions[b + 1]!;
      const pz = dim > 2 ? positions[b + 2]! : 0;
      for (const w of wells) {
        const wm = w.m ?? 1;
        softPullToward(
          px,
          py,
          pz,
          w.x,
          w.y,
          w.z ?? 0,
          dim,
          eps,
          gamma * wm,
          out,
          b,
        );
      }
    }
    applyResponseMass(out, n, dim, m);
    return;
  }

  if (mode === "mutual") {
    // Local mass-normalized force, then /m response.
    //   force_i = γ · Σ_j m_j r̂_ij / (m_ref + Σ_j m_j)
    //   g_i     = force_i / m_i
    // |force| ≤ γ, so |g| ≤ γ/m independent of neighbor count and n.
    const radius =
      params.gravityRadius > 0 ? params.gravityRadius : params.neighborRadius;
    const r2 = radius * radius;
    const hash = opts?.spatialHash;
    const mRef = m;

    for (let i = 0; i < n; i++) {
      const b = i * dim;
      const px = positions[b]!;
      const py = positions[b + 1]!;
      const pz = dim > 2 ? positions[b + 2]! : 0;

      let ax = 0,
        ay = 0,
        az = 0;
      let wAcc = 0;

      const addJ = (j: number) => {
        if (j === i) return;
        const jb = j * dim;
        const dx = positions[jb]! - px;
        const dy = positions[jb + 1]! - py;
        const dz = dim > 2 ? positions[jb + 2]! - pz : 0;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const inv = 1 / (eps + dist);
        ax += m * dx * inv;
        ay += m * dy * inv;
        az += m * dz * inv;
        wAcc += m;
      };

      if (hash) {
        hash.forNeighbors(i, positions, dim, r2, (j) => addJ(j));
      } else {
        for (let j = 0; j < n; j++) addJ(j);
      }

      const denom = mRef + wAcc;
      if (denom <= 1e-15 || wAcc <= 0) continue;
      const s = gamma / denom;
      out[b]! += ax * s;
      out[b + 1]! += ay * s;
      if (dim > 2) out[b + 2]! += az * s;
    }
    applyResponseMass(out, n, dim, m);
  }
}

export function motionNeedsHash(
  params: Pick<SimParams, "gravityMode">,
): boolean {
  return params.gravityMode === "mutual";
}

export function motionIsIdentity(
  params: Pick<SimParams, "motionMode" | "gravityMode" | "model">,
): boolean {
  const inertial =
    params.motionMode === "inertial" && params.model !== "reynolds";
  return !inertial && params.gravityMode === "none";
}
